if (!customElements.get('newsletter-discount-popup')) {
  customElements.define(
    'newsletter-discount-popup',
    class NewsletterDiscountPopup extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.dialog = this.querySelector('.ndp__dialog');
        this.form = this.querySelector('form');
        this.fields = this.querySelector('[data-popup-fields]');
        this.error = this.querySelector('[data-popup-error]');
        this.success = this.querySelector('[data-popup-success]');
        this.submitButton = this.querySelector('[data-submit-button]');
        this.submitLabel = this.querySelector('[data-submit-label]');
        this.submitSpinner = this.querySelector('[data-submit-spinner]');
        this.copyButton = this.querySelector('[data-copy-code]');
        this.previousFocus = null;
        this.openTimer = null;

        this.querySelectorAll('[data-popup-close]').forEach((button) => {
          button.addEventListener('click', () => this.close());
        });
        this.form?.addEventListener('submit', (event) => this.handleSubmit(event));
        this.copyButton?.addEventListener('click', () => this.copyCode());
        document.addEventListener('keydown', (event) => this.handleKeydown(event));
        document.addEventListener('shopify:section:select', (event) => this.handleSectionSelect(event));

        const hasServerSuccess = this.success && !this.success.hidden;
        if (this.isDesignMode() || hasServerSuccess) {
          this.open();
          if (hasServerSuccess) this.markSubscribed();
          return;
        }

        if (!this.canOpen()) return;
        const delay = Math.max(0, Number(this.dataset.delay || 0)) * 1000;
        this.openTimer = window.setTimeout(() => this.open(), delay);
      }

      disconnectedCallback() {
        if (this.openTimer) window.clearTimeout(this.openTimer);
      }

      isDesignMode() {
        return this.dataset.designMode === 'true' || Boolean(window.Shopify?.designMode);
      }

      storageKey(type) {
        return `loopluna:newsletter-discount:${this.dataset.sectionId}:${type}`;
      }

      getStored(storage, key) {
        try {
          return storage.getItem(key);
        } catch (error) {
          return null;
        }
      }

      setStored(storage, key, value) {
        try {
          storage.setItem(key, value);
        } catch (error) {
          // The popup still works when browser storage is unavailable.
        }
      }

      canOpen() {
        if (this.getStored(window.localStorage, this.storageKey('subscribed')) === 'true') return false;

        switch (this.dataset.repeat) {
          case 'always':
            return true;
          case 'session':
            return this.getStored(window.sessionStorage, this.storageKey('dismissed')) !== 'true';
          default: {
            const dismissedAt = Number(this.getStored(window.localStorage, this.storageKey('dismissed')) || 0);
            return !dismissedAt || Date.now() - dismissedAt >= 24 * 60 * 60 * 1000;
          }
        }
      }

      rememberDismissal() {
        if (this.isDesignMode()) return;

        if (this.dataset.repeat === 'session') {
          this.setStored(window.sessionStorage, this.storageKey('dismissed'), 'true');
        } else if (this.dataset.repeat !== 'always') {
          this.setStored(window.localStorage, this.storageKey('dismissed'), String(Date.now()));
        }
      }

      markSubscribed() {
        this.setStored(window.localStorage, this.storageKey('subscribed'), 'true');
      }

      open() {
        if (!this.hidden && this.classList.contains('is-open')) return;
        this.previousFocus = document.activeElement;
        this.hidden = false;
        window.requestAnimationFrame(() => this.classList.add('is-open'));
        document.body.classList.add('ndp-open');
        this.dialog?.focus();
      }

      close() {
        if (this.hidden) return;
        this.rememberDismissal();
        this.classList.remove('is-open');
        document.body.classList.remove('ndp-open');
        window.setTimeout(() => {
          this.hidden = true;
        }, 180);
        this.previousFocus?.focus?.();
      }

      handleKeydown(event) {
        if (event.key === 'Escape' && !this.hidden) this.close();
      }

      handleSectionSelect(event) {
        if (event.detail?.sectionId === this.dataset.sectionId) this.open();
      }

      setSubmitting(submitting) {
        if (!this.submitButton) return;
        this.submitButton.disabled = submitting;
        this.submitButton.setAttribute('aria-busy', String(submitting));
        if (this.submitLabel) this.submitLabel.hidden = submitting;
        if (this.submitSpinner) this.submitSpinner.hidden = !submitting;
      }

      showError(message) {
        if (!this.error) return;
        this.error.textContent = message || 'Unable to subscribe right now. Please try again.';
        this.error.hidden = false;
      }

      showSuccess() {
        if (this.fields) this.fields.hidden = true;
        if (this.error) this.error.hidden = true;
        if (this.success) this.success.hidden = false;
        this.markSubscribed();
        this.success?.querySelector('[tabindex="-1"]')?.focus();
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (!this.form?.reportValidity() || this.submitButton?.disabled) return;

        this.setSubmitting(true);
        if (this.error) this.error.hidden = true;

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            body: new FormData(this.form),
            headers: { Accept: 'text/html' },
            credentials: 'same-origin',
          });
          const responseHtml = await response.text();
          const responseDocument = new DOMParser().parseFromString(responseHtml, 'text/html');
          const returnedForm = responseDocument.getElementById(this.form.id);
          const returnedError = returnedForm?.querySelector('[data-popup-error], .form-message--main');
          const errorText = returnedError?.textContent?.trim() || '';
          const subscribed =
            response.url.includes('customer_posted=true') ||
            Boolean(returnedForm?.querySelector('[data-popup-success]:not([hidden])'));
          const alreadySubscribed = /already|subscribed|taken/i.test(errorText);

          if (subscribed || alreadySubscribed) {
            this.showSuccess();
            return;
          }

          this.showError(errorText);
        } catch (error) {
          this.showError();
        } finally {
          this.setSubmitting(false);
        }
      }

      async copyCode() {
        if (!this.copyButton) return;
        const code = this.copyButton.dataset.code || '';
        const codeInput = this.querySelector('[data-code-input]');

        try {
          if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(code);
          } else {
            throw new Error('Clipboard API unavailable');
          }
        } catch (error) {
          codeInput?.select();
          codeInput?.setSelectionRange(0, codeInput.value.length);
          document.execCommand('copy');
        }

        this.copyButton.textContent = this.copyButton.dataset.copiedText || 'Copied';
        window.setTimeout(() => {
          this.copyButton.textContent = this.copyButton.dataset.defaultText || 'Copy Code';
        }, 2000);
      }
    }
  );
}
